import React, { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { v4 as uuid } from 'uuid';
import { Advice } from '@Sage/hoi-poi-ui';
import { useWeb5Context } from 'lib/Web5Context';
import { isAtSeatLimit } from 'utils/users';
import { EMAIL_REGEX } from 'shards/Crons/constants';
import { types, EMAIL_VALIDATION_ERRORS, ROLES } from '../../constants';

const editFormInitialState = {
    values: {},
    errors: {},
    touched: {},
};

const addFormInitialState = {
    values: [{}],
    errors: [{}],
    touched: [{}],
};

const initialState = {
    loading: true,
    data: [],
    dragging: null,
    modal: {
        isOpen: false,
        type: undefined,
        [types.MODAL_EDIT]: editFormInitialState,
        [types.MODAL_ADD_SINGLE]: editFormInitialState,
        [types.MODAL_ADD]: addFormInitialState,
    },
};

const reducer = (state, action) => {
    switch (true) {
        case action.type === types.LOADING:
            return { ...state, loading: action.value };
        case action.type === types.DATA:
            return { ...state, data: action.value, dragging: null, loading: false };
        case action.type === types.DRAG_ENTER:
            return { ...state, dragging: action.value };
        case action.type === types.CLOSE_MODAL:
            return {
                ...state,
                modal: {
                    ...state.modal,
                    isOpen: false,
                    [types.MODAL_EDIT]: editFormInitialState,
                    [types.MODAL_ADD]: addFormInitialState,
                },
            };
        case action.type === types.OPEN_MODAL && action.value === types.MODAL_EDIT:
        case action.type === types.OPEN_MODAL && action.value === types.MODAL_ADD_SINGLE:
            return {
                ...state,
                modal: {
                    ...state.modal,
                    isOpen: true,
                    type: action.value,
                    [action.value]: {
                        ...state.modal[action.value],
                        values: action.payload || {},
                    },
                },
            };
        case action.type === types.OPEN_MODAL && action.value === types.MODAL_ADD:
            return {
                ...state,
                modal: {
                    ...state.modal,
                    isOpen: true,
                    type: action.value,
                    [types.MODAL_ADD]: {
                        values: [{}],
                        errors: [{}],
                        touched: [{}],
                    },
                },
            };
        case action.type === types.UPDATE_MODAL:
            return {
                ...state,
                modal: {
                    ...state.modal,
                    [action.value]: {
                        ...state.modal[action.value],
                        ...action.payload,
                    },
                },
            };
        default:
            return state;
    }
};

const useOnboarding = ({
    getData,
    getDataCallback,
    handleError,
    handleSetCount,
    setData,
    parseData,
    // User Management
    customEmailValidation,
    userRoles = [],
    settings,
    canEditUsersWithSpecialRoles = false,
    isUserManagement = false,
}) => {
    const isFirstLoad = useRef(true);
    const isCancelled = useRef(false);
    const [state, dispatch] = useReducer(reducer, initialState);
    const { t } = useWeb5Context();

    const handleGetData = useCallback(() => {
        getData &&
            getData()
                .then((data) => {
                    const newData = getDataCallback ? getDataCallback(data) : data;
                    dispatch({ type: types.DATA, value: newData });
                    handleSetCount && handleSetCount(newData.length);
                })
                .catch((e) => {
                    console.error(e);
                    handleError &&
                        handleError({ title: e.data?.code, description: e.data?.message });
                })
                .finally(() => {
                    if (!isCancelled.current) dispatch({ type: types.LOADING, value: false });
                });
    }, [getData, getDataCallback, handleError, handleSetCount]);

    useEffect(() => {
        if (!isFirstLoad.current) return;
        isFirstLoad.current = false;
        isCancelled.current = false;
        handleGetData();
        return () => {
            isCancelled.current = true;
        };
    }, [getData, getDataCallback, handleError, handleGetData, handleSetCount]);

    // ************************************************************************
    // DATA VALIDATION METHODS
    // ************************************************************************
    const isRequired = useCallback((value) => (!value ? t('helptext_required_field') : false), [t]);

    const isEmail = useCallback(
        (email) => {
            return !EMAIL_REGEX.test(email.trim())
                ? t('error_validation_email_wrong_format')
                : false;
        },
        [t],
    );

    const isDuplicatedEmail = useCallback(
        (email, index) => {
            let currentValues = [...state.data];

            if (state.modal.type === types.MODAL_ADD) {
                currentValues = [
                    ...currentValues,
                    ...state.modal[types.MODAL_ADD].values.filter((el, i) => i !== index), // Skip the current value
                ];
            }

            if (state.modal.type === types.MODAL_EDIT) {
                currentValues = currentValues.filter(
                    (el) => el.id !== state.modal[types.MODAL_EDIT].values.id,
                );
            }

            const isDuplicated = !currentValues.every((value) => value?.email !== email);

            return isDuplicated ? t('label_duplicated_email') : false;
        },
        [t, state],
    );

    const validateEmail = useCallback(
        (email) => {
            return new Promise((resolve) => {
                if (customEmailValidation) {
                    customEmailValidation(email).catch((error) => {
                        resolve(
                            error?.code === EMAIL_VALIDATION_ERRORS.USED
                                ? t('label_duplicated_email')
                                : error?.code === EMAIL_VALIDATION_ERRORS.NOT_VALID
                                  ? t('error_validation_email_wrong_format')
                                  : 'Error',
                        );
                    });
                } else {
                    resolve();
                }
            });
        },
        [customEmailValidation, t],
    );

    const formHasErrors = useCallback(
        (modal) => {
            const form = state.modal[modal];
            const errors = form.errors;

            switch (modal) {
                case types.MODAL_EDIT:
                case types.MODAL_ADD_SINGLE:
                    return Object.values(errors).filter(Boolean).length > 0;
                case types.MODAL_ADD:
                    return !errors.every(
                        (error) => Object.values(error).filter(Boolean).length === 0,
                    );
                default:
                    break;
            }
        },
        [state],
    );

    const validateFields = useCallback((values, validateField, formSchema, index) => {
        if (!validateField && !formSchema) return {};
        return formSchema.reduce((obj, field) => {
            const { name } = field;
            return {
                ...obj,
                [name]: validateField(name, values ? values[name] : undefined, index),
            };
        }, {});
    }, []);

    const validateAddForm = useCallback(
        (values, validateField, formSchema) => {
            if (!validateField && !formSchema) return false;
            const errors = values.reduce(
                (arr, value, index) => [
                    ...arr,
                    validateFields(value, validateField, formSchema, index),
                ],
                [],
            );

            const touched = errors.reduce(
                (arr, error) => [
                    ...arr,
                    Object.keys(error).reduce((obj, key) => ({ ...obj, [key]: true }), {}),
                ],
                [],
            );

            dispatch({
                type: types.UPDATE_MODAL,
                value: types.MODAL_ADD,
                payload: {
                    errors,
                    touched,
                },
            });

            return errors.every((error) => Object.values(error).filter(Boolean).length === 0);
        },
        [dispatch, validateFields],
    );

    // ************************************************************************
    // DATA EDITING METHODS
    // ************************************************************************
    const handleUpdate = useCallback(
        (data, callback) => {
            dispatch({ type: types.LOADING, value: true });
            if (setData) {
                // Remove temporary Id of new items
                let newData = data.reduce((arr, item) => {
                    let newItem = item;
                    if (item.temp) {
                        const { temp, id, ...restItem } = item;
                        newItem = restItem;
                    }
                    arr.push(newItem);
                    return arr;
                }, []);

                newData = parseData ? parseData(newData) : newData;

                setData(newData)
                    .then(() => {
                        dispatch({ type: types.DATA, value: data });
                        handleGetData();
                        handleSetCount && handleSetCount(newData.length);
                        callback && callback();
                    })
                    .catch((error) => {
                        console.error(error);
                    });
            } else {
                dispatch({ type: types.DATA, value: data });
            }
        },
        [setData, parseData, handleSetCount, handleGetData],
    );

    const handleDelete = useCallback(
        (row) => {
            const newData = state.data.reduce(
                (arr, item) => (item.id === row.id ? arr : [...arr, item]),
                [],
            );
            handleUpdate(newData);
        },
        [handleUpdate, state.data],
    );

    const handleDragEnter = useCallback((event) => {
        dispatch({ type: types.DRAG_ENTER, value: event.overIndex });
    }, []);

    const handleDragEnd = useCallback(
        (event, rows) =>
            new Promise((resolve) => {
                const newRows = rows.rowDataBefore;
                const row = newRows[state.dragging];
                const toIndex = event.overIndex;
                newRows.splice(state.dragging, 1);
                newRows.splice(toIndex, 0, row);
                handleUpdate(newRows);
                resolve();
            }),
        [handleUpdate, state.dragging],
    );

    const handleEdit = useCallback(
        (payload) => {
            dispatch({
                type: types.OPEN_MODAL,
                value: types.MODAL_EDIT,
                payload,
            });
        },
        [dispatch],
    );

    const handleEditFormChange = useCallback(
        ({ field, value }, validateField) => {
            const modalType = types.MODAL_EDIT;
            const form = state.modal[modalType];
            const error = validateField && validateField(field, value);

            dispatch({
                type: types.UPDATE_MODAL,
                value: modalType,
                payload: {
                    values: {
                        ...form.values,
                        [field]: value,
                    },
                    errors: {
                        ...form.errors,
                        [field]: error,
                    },
                    touched: {
                        ...form.touched,
                        [field]: true,
                    },
                },
            });
        },
        [dispatch, state.modal],
    );

    const handleEditFormBlur = useCallback(
        ({ field }) => {
            const modalType = types.MODAL_EDIT;
            const form = state.modal[modalType];
            dispatch({
                type: types.UPDATE_MODAL,
                value: modalType,
                payload: {
                    touched: {
                        ...form.touched,
                        [field]: true,
                    },
                },
            });
        },
        [dispatch, state.modal],
    );

    const handleAddFormChange = useCallback(
        ({ value: values, newValue, index, action }, validateField, formSchema) => {
            const newError = validateFields(newValue, validateField, formSchema);
            const { errors: oldErrors, touched: oldTouched } = state.modal[types.MODAL_ADD];
            const errors = [...oldErrors];
            const touched = [...oldTouched];

            if (action && action === 'remove') {
                // REMOVED MULTIPLIER ITEM
                errors.splice(index, 1);
                touched.splice(index, 1);
            } else if (Number.isInteger(index)) {
                // UPDATED MULTIPLIER ITEM
                if (!touched[index]) touched[index] = {};
                errors[index] = newError;
            } else {
                // ADDED MULTIPLIER ITEM
                errors.push({});
                touched.push({});
            }

            dispatch({
                type: types.UPDATE_MODAL,
                value: types.MODAL_ADD,
                payload: {
                    values,
                    errors,
                    touched,
                },
            });
        },
        [validateFields, state.modal, dispatch],
    );

    const handleAddFormBlur = useCallback(
        ({ index, field }) => {
            const modalType = types.MODAL_EDIT;
            const { touched: oldTouched } = state.modal[modalType];

            const touched = [...oldTouched];
            const oldTouchedField = touched[index] || {};
            const touchedField = { ...oldTouchedField, [field.name]: true };
            touched[index] = touchedField;

            dispatch({
                type: modalType,
                value: types.MODAL_ADD,
                payload: {
                    touched,
                },
            });
        },
        [dispatch, state.modal],
    );

    // ************************************************************************
    // USER MANAGEMENT RELATED STUFF
    // ************************************************************************
    const parsedRoles = useMemo(() => {
        // We assume that the role that has min=-1 & max=-1 is the default role (Sales Rep)
        // min=-1 & max=1 would be the Manager
        // and min=1 & max=1 would be the Admin
        return userRoles.reduce((obj, role) => {
            let key;
            switch (true) {
                case role.min === -1 && role.max === -1:
                    key = ROLES.SALES_REP;
                    break;
                case role.min === -1 && role.max === 1:
                    key = ROLES.MANAGER;
                    break;
                case role.min === 1 && role.max === 1:
                    key = ROLES.ADMIN;
                    break;
                default:
                    break;
            }

            return {
                ...obj,
                [key]: role,
            };
        }, {});
    }, [userRoles]);

    const usersPerSpecialRole = useMemo(() => {
        const newUsersPerRole = userRoles.reduce((obj, role) => {
            const { id, min, max } = role;
            if (min === -1 && max === -1) {
                return obj;
            } else {
                const warningMsg =
                    min === 1 && max === 1
                        ? 'label_change_admin_role'
                        : 'label_change_manager_role';
                const userInfo = state.data.filter((user) => user?.roleId === id)[0];
                return {
                    ...obj,
                    [id]: userInfo
                        ? {
                              ...userInfo,
                              warningMsg,
                          }
                        : null,
                };
            }
        }, {});
        return newUsersPerRole;
    }, [state, userRoles]);

    const roleOptions = useMemo(() => {
        return userRoles.reduce((arr, item) => {
            return [
                ...arr,
                {
                    label: item.value,
                    subLabel: t(item.description),
                    value: item.id,
                    isDisabled:
                        (!canEditUsersWithSpecialRoles &&
                            usersPerSpecialRole[item.id]?.status === 1) ||
                        (canEditUsersWithSpecialRoles &&
                            state.modal.type === types.MODAL_ADD &&
                            [parsedRoles[ROLES.MANAGER]?.id, parsedRoles[ROLES.ADMIN]?.id].includes(
                                item.id,
                            )),
                },
            ];
        }, []);
    }, [
        canEditUsersWithSpecialRoles,
        parsedRoles,
        state.modal.type,
        userRoles,
        usersPerSpecialRole,
        t,
    ]);

    const roleDefaultValue = useMemo(() => {
        return roleOptions && roleOptions.length === 1
            ? roleOptions[0]
            : roleOptions && roleOptions.length > 1
              ? roleOptions.filter(
                    (option) => option.value === parsedRoles?.[ROLES.SALES_REP]?.id,
                )?.[0]
              : undefined;
    }, [parsedRoles, roleOptions]);

    const userHandleAddFormChange = useCallback(
        ({ value: values, newValue, index, action, field }, validateField, formSchema) => {
            const newError = validateFields(newValue, validateField, formSchema, index);
            const { errors: oldErrors, touched: oldTouched } = state.modal[types.MODAL_ADD];
            const errors = [...oldErrors];
            const touched = [...oldTouched];

            if (action && action === 'remove') {
                // REMOVED MULTIPLIER ITEM
                errors.splice(index, 1);
                touched.splice(index, 1);
            } else if (Number.isInteger(index)) {
                // UPDATED MULTIPLIER ITEM
                if (!touched[index]) touched[index] = {};

                // If we got a "Duplicated email" error in a previous validation we keep it
                if (field.name === 'role' && oldErrors[index].email === t('label_duplicated_email'))
                    newError.email = t('label_duplicated_email');
                errors[index] = newError;

                // If the updated user is ADMIN or MANAGER the other users will be SALES REP
                if (
                    newValue?.role?.value === parsedRoles[ROLES.MANAGER]?.id ||
                    newValue?.role?.value === parsedRoles[ROLES.ADMIN]?.id
                ) {
                    values = values.reduce((arr, item, i) => {
                        return [
                            ...arr,
                            i === index
                                ? item
                                : {
                                      email: item?.email,
                                      role: {
                                          label: parsedRoles[ROLES.SALES_REP]?.value,
                                          value: parsedRoles[ROLES.SALES_REP]?.id,
                                      },
                                  },
                        ];
                    }, []);
                }
            } else {
                // ADDED MULTIPLIER ITEM
                if (isUserManagement) {
                    // Set default value to new multiplier item
                    values.pop();
                    values.push({ role: roleDefaultValue });
                }
                errors.push({});
                touched.push({});
            }

            dispatch({
                type: types.UPDATE_MODAL,
                value: types.MODAL_ADD,
                payload: {
                    values,
                    errors,
                    touched,
                },
            });
        },
        [validateFields, state.modal, t, parsedRoles, isUserManagement, roleDefaultValue],
    );

    const userHandleAddFormBlur = useCallback(
        ({ newValue, index, field }) => {
            const { errors: oldErrors, touched: oldTouched } = state.modal[types.MODAL_ADD];

            const touched = [...oldTouched];
            const oldTouchedField = touched[index] || {};
            const touchedField = { ...oldTouchedField, [field.name]: true };
            touched[index] = touchedField;

            const errors = [...oldErrors];
            const oldErrorField = errors[index] || {};

            if (field.name === 'email' && newValue.email && !oldErrorField.email) {
                validateEmail(newValue.email).then((response) => {
                    const errorField = { ...oldErrorField, email: response };
                    errors[index] = errorField;
                    dispatch({
                        type: types.UPDATE_MODAL,
                        value: types.MODAL_ADD,
                        payload: {
                            touched,
                            errors,
                        },
                    });
                });
            } else {
                dispatch({
                    type: types.UPDATE_MODAL,
                    value: types.MODAL_ADD,
                    payload: {
                        touched,
                    },
                });
            }
        },
        [dispatch, state.modal, validateEmail],
    );

    const userHandleAddFormSave = useCallback(
        (validateField, formSchema, callback) => {
            const form = state.modal[types.MODAL_ADD];
            const addedUsers = form.values;
            const currentUsers = state.data;
            let updatedAdminUser = false;
            let updatedManagerUser = false;

            if (!validateAddForm(addedUsers, validateField, formSchema)) return;

            let newUsers = addedUsers.reduce(
                (arr, item) => {
                    updatedAdminUser =
                        item.role?.value === parsedRoles[ROLES.ADMIN]?.id &&
                        usersPerSpecialRole[item.role?.value]?.id;
                    updatedManagerUser =
                        item.role?.value === parsedRoles[ROLES.MANAGER]?.id &&
                        usersPerSpecialRole[item.role?.value]?.id;

                    return [
                        ...arr,
                        {
                            id: uuid(),
                            temp: true,
                            email: item.email,
                            roleId: item.role?.value,
                            role: item.role?.label,
                            status: 0,
                        },
                    ];
                },
                [...currentUsers],
            );

            // Demote the previous admin/manager user if a new one is set
            if (updatedAdminUser || updatedManagerUser) {
                const salesRep = parsedRoles[ROLES.SALES_REP];
                newUsers = newUsers.reduce((arr, user) => {
                    let newUser;
                    if (user.id === updatedAdminUser || user.id === updatedManagerUser) {
                        newUser = {
                            ...user,
                            role: salesRep.value,
                            roleId: salesRep.id,
                        };
                    } else {
                        newUser = user;
                    }
                    return [...arr, newUser];
                }, []);
            }

            handleUpdate(newUsers, callback);
            dispatch({ type: types.CLOSE_MODAL });
        },
        [
            state.modal,
            state.data,
            validateAddForm,
            handleUpdate,
            dispatch,
            parsedRoles,
            usersPerSpecialRole,
        ],
    );

    const userHandleEditFormBlur = useCallback(
        ({ field, value }) => {
            const form = state.modal[types.MODAL_EDIT];
            const { id, email } = form.values;
            const oldEmail = state.data.filter((user) => user.id === id)[0]?.email;

            if (field === 'email' && oldEmail !== email) {
                validateEmail(value).then((response) => {
                    dispatch({
                        type: types.UPDATE_MODAL,
                        value: types.MODAL_EDIT,
                        payload: {
                            touched: {
                                ...form.touched,
                                [field]: true,
                            },
                            errors: {
                                ...form.errors,
                                [field]: response,
                            },
                        },
                    });
                });
            } else {
                dispatch({
                    type: types.UPDATE_MODAL,
                    value: types.MODAL_EDIT,
                    payload: {
                        touched: {
                            ...form.touched,
                            [field]: true,
                        },
                    },
                });
            }
        },
        [dispatch, state.data, state.modal, validateEmail],
    );

    const userHandleEditFormSave = useCallback(() => {
        const { email, role, id, ...otherFields } = state.modal[types.MODAL_EDIT].values;
        const currentUsers = state.data;
        const updatedAdminUser = role?.value === parsedRoles[ROLES.ADMIN]?.id;
        const updatedManagerUser = role?.value === parsedRoles[ROLES.MANAGER]?.id;
        const salesRepRole = parsedRoles[ROLES.SALES_REP];
        const adminRole = parsedRoles[ROLES.ADMIN];
        const managerRole = parsedRoles[ROLES.MANAGER];

        let newUsers = currentUsers.reduce((arr, user) => {
            let newUser;

            if (user.id === id) {
                newUser = {
                    ...user,
                    ...otherFields,
                    email,
                    role: role.label,
                    roleId: role.value,
                };
            } else if (updatedAdminUser && adminRole && user.roleId === adminRole.id) {
                newUser = {
                    ...user,
                    role: salesRepRole.value,
                    roleId: salesRepRole.id,
                };
            } else if (updatedManagerUser && managerRole && user.roleId === managerRole.id) {
                newUser = {
                    ...user,
                    role: salesRepRole.value,
                    roleId: salesRepRole.id,
                };
            } else {
                newUser = user;
            }

            return [...arr, newUser];
        }, []);

        handleUpdate(newUsers);
        dispatch({ type: types.CLOSE_MODAL });
    }, [state.modal, state.data, parsedRoles, handleUpdate, dispatch]);

    const userGetAdvice = useCallback(
        (value) => {
            if (!value) return null;
            const { role, id } = value;
            const specialUser = usersPerSpecialRole[role?.value];
            const advices = [];

            if (
                state.modal.type === types.MODAL_ADD &&
                roleOptions.length > 1 &&
                isUserManagement
            ) {
                advices.push(t('label_permisions_admin_manager_when_accept_invite'));
            }

            if (specialUser && specialUser?.id !== id) {
                advices.push(t(specialUser.warningMsg, [specialUser.fullname]));
            }

            return advices.length > 0 ? (
                <Advice
                    type="warning"
                    showIcon
                    showCollapse={false}
                    overrides={{
                        Text: {
                            style: {
                                display: 'flex',
                                flexFlow: 'column nowrap',
                            },
                        },
                    }}
                >
                    {advices.map((advice, index) => (
                        <span key={index}>{advice}</span>
                    ))}
                </Advice>
            ) : null;
        },
        [isUserManagement, roleOptions.length, state.modal.type, t, usersPerSpecialRole],
    );

    const userManagementPermissions = useMemo(() => {
        if (!settings) return {};
        const { billingPlatformPlanCode, config, licenses, permission, userData } = settings;
        const {
            allowAddLicenses,
            allowRoles: canManageRoles,
            product,
            totalPurchased,
            totalUser,
        } = config;
        const { trialPeriodDaysRemaining, manageUsersFromWeb = 0 } = userData;

        let activateUserTooltip = null;

        const hasUserManagement = !!manageUsersFromWeb && permission.canConfigImplementation;

        const hasAppStore = billingPlatformPlanCode === 'appstore';

        let canActivateUser =
            (product?.includes('free') && parseInt(trialPeriodDaysRemaining, 10) > 0) ||
            !isAtSeatLimit({
                totalBillableUsers: totalUser,
                totalUsersPurchased: totalPurchased,
                isFreeImpl: false,
            });

        const canInviteUser = canActivateUser;

        const canAddLicenses =
            (product !== licenses.KITDIGITAL ||
                (product === licenses.KITDIGITAL && totalPurchased < 3)) &&
            allowAddLicenses;

        if (!canActivateUser && product === licenses.KITDIGITAL) {
            activateUserTooltip = t('label_max_3_licences_kit_digital');
        } else if (!canActivateUser) {
            activateUserTooltip = t('cfm_label_tooltip_users_limit');
        }

        // If payment gateway is Apple Store
        if (hasAppStore) {
            if (totalUser === totalPurchased) {
                canActivateUser = false;
                activateUserTooltip = t('label_max_licences_apple_payment');
            }
        }

        return {
            activateUserTooltip,
            hasUserManagement,
            canActivateUser,
            canAddLicenses,
            canInviteUser,
            canManageRoles,
        };
    }, [settings, t]);

    return {
        state,
        types,
        dispatch,
        handleDragEnter,
        handleDragEnd,
        handleDelete,
        handleUpdate,
        handleEdit,
        handleEditFormChange,
        handleEditFormBlur,
        handleAddFormChange,
        handleAddFormBlur,
        handleGetData,
        isRequired,
        isEmail,
        isDuplicatedEmail,
        formHasErrors,
        validateEmail,
        validateFields,
        validateAddForm,
        userManagement: {
            parsedRoles,
            roleOptions,
            roleTypes: ROLES,
            roleDefaultValue,
            handleAddFormChange: userHandleAddFormChange,
            handleAddFormBlur: userHandleAddFormBlur,
            handleAddFormSave: userHandleAddFormSave,
            handleEditFormBlur: userHandleEditFormBlur,
            handleEditFormSave: userHandleEditFormSave,
            getAdvice: userGetAdvice,
            permissions: userManagementPermissions,
            usersPerSpecialRole,
        },
    };
};

export default useOnboarding;
